import { Dialog } from '@angular/cdk/dialog';
import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs';
import { InformationModalComponent } from 'src/app/components/modals/information-modal/information-modal.component';
import { CertificateDto } from 'src/app/models/device.model';
import { ProfileType } from 'src/app/models/user.model';
import { CertificateService } from 'src/app/services/certificate.service';
import { humanizeEnumValue } from 'src/app/utils/enum-display';
import {
  formatRelativeTimestamp,
  formatTimestamp,
} from 'src/utils/general-utils';

@Component({
  selector: 'app-device-certificates',
  templateUrl: './device-certificates.component.html',
  styleUrl: './device-certificates.component.scss',
  standalone: false,
})
export class DeviceCertificatesComponent {
  private readonly translate = inject(TranslateService);
  private readonly dialog = inject(Dialog);
  private readonly certificateService = inject(CertificateService);

  @Input() certificates?: CertificateDto[] | null;
  @Input() canRevoke = false;
  @Output() certificateRevoked = new EventEmitter<string>();

  revokingThumbprint?: string;
  revokeError?: string;

  /** Valid ones first, then revoked; each group with the latest registration first. */
  get sortedCertificates(): CertificateDto[] {
    return [...(this.certificates ?? [])].sort(
      (a, b) =>
        Number(this.isRevoked(a)) - Number(this.isRevoked(b)) ||
        (b.createdAt ?? '').localeCompare(a.createdAt ?? ''),
    );
  }

  get validCount(): number {
    return (this.certificates ?? []).filter((c) => !this.isRevoked(c)).length;
  }

  get revokedCount(): number {
    return (this.certificates ?? []).filter((c) => this.isRevoked(c)).length;
  }

  isRevoked(certificate: CertificateDto): boolean {
    return !!certificate.revokedAt;
  }

  isExpired(certificate: CertificateDto): boolean {
    return (
      !!certificate.expiresAt && Date.parse(certificate.expiresAt) <= Date.now()
    );
  }

  revoke(certificate: CertificateDto): void {
    const thumbprint = certificate.thumbprint;
    if (!thumbprint || this.revokingThumbprint) {
      return;
    }

    const dialogRef = this.dialog.open(InformationModalComponent, {
      data: {
        headerText: this.translate.instant('device_certificates.revoke.title'),
        bodyText: this.translate.instant('device_certificates.revoke.body', {
          thumbprint,
        }),
        cancelable: true,
        confirmButtonText: this.translate.instant(
          'device_certificates.revoke.confirm',
        ),
      },
    });

    dialogRef.closed.pipe(take(1)).subscribe((result) => {
      if ((result as { cancel?: boolean } | undefined)?.cancel !== false) {
        return;
      }
      this.revokingThumbprint = thumbprint;
      this.revokeError = undefined;
      this.certificateService
        .revokeCertificate(thumbprint)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.revokingThumbprint = undefined;
            this.certificateRevoked.emit(thumbprint);
          },
          error: () => {
            this.revokingThumbprint = undefined;
            this.revokeError = this.translate.instant(
              'device_certificates.revoke.failed',
            );
          },
        });
    });
  }

  displayName(profile?: Partial<ProfileType>): string | undefined {
    const fullName = [profile?.firstName, profile?.lastName]
      .filter((part): part is string => !!part?.trim())
      .join(' ');

    return fullName || profile?.email?.trim() || undefined;
  }

  formatRole(role?: string): string {
    return role ? humanizeEnumValue(role) : '';
  }

  formatTime(timestamp?: string): string {
    return formatTimestamp(timestamp);
  }

  formatTimeAgo(timestamp?: string): string {
    const locale =
      this.translate.currentLang || this.translate.getDefaultLang() || 'en';
    return formatRelativeTimestamp(timestamp, locale);
  }

  trackCertificate(index: number, certificate: CertificateDto): string {
    return certificate.thumbprint ?? `${index}`;
  }
}
